"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetStarted() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-started" ref={sectionRef} className="py-24 md:py-32 bg-background" style={{
    backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542563/img/geometric_light_texture_background.png)",
    backgroundSize: "cover",
    backgroundPosition: "center"
  }}>
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid lg:grid-cols-[3fr_2fr] gap-12 items-center">
          {}
          <div className={`space-y-6 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-[48px] leading-[1.2] font-bold tracking-[-0.2px] text-foreground">
              Get Started
            </h2>
            <p className="text-[16px] leading-[1.6] text-muted-foreground max-w-xl">
              Ready to transform your financial management with cloud accounting? Fill out the form and our team will get back to you within 24 hours to discuss your specific needs.
            </p>
            <div className="pt-4">
              <Button variant="outline" size="lg" className="rounded-[var(--radius-full)] px-8 hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)]">
                Learn More About Our Services
              </Button>
            </div>
          </div>

          {}
          <div className={`transition-all duration-[var(--duration-medium)] delay-150 ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider className="bg-surface border border-border rounded-[var(--radius-sm)] p-8 shadow-medium" formId="get_started_cloudaccountingpage" sectionName="get-started-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company_name: "",
            business_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            company_name: {
              required: true
            },
            business_type: {
              required: true
            },
            message: {
              required: false
            }
          }} submitText="Get Started" successMessage="Thank you for reaching out! We'll contact you within 24 hours to discuss your cloud accounting needs." errorMessage="We couldn't submit your request. Please check your information and try again.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-[hsl(147,60%,95%)] border border-[hsl(147,60%,70%)] rounded-[var(--radius-md)]">
        <p className="text-[hsl(147,60%,20%)] text-[16px] leading-[1.6]">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-[var(--radius-full)]">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must not exceed 50 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="rounded-[var(--radius-sm)] focus-visible:ring-[hsl(221,83%,53%)] focus-visible:ring-[3px] focus-visible:border-[hsl(221,83%,53%)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormDescription className="text-[14px]">Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="rounded-[var(--radius-sm)] focus-visible:ring-[hsl(221,83%,53%)] focus-visible:ring-[3px] focus-visible:border-[hsl(221,83%,53%)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormDescription className="text-[14px]">We'll use this to contact you.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\+\-\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 1234 567890" className="rounded-[var(--radius-sm)] focus-visible:ring-[hsl(221,83%,53%)] focus-visible:ring-[3px] focus-visible:border-[hsl(221,83%,53%)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormDescription className="text-[14px]">Include country code if outside the UK.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organisation</FormLabel>
              <FormControl>
                <Input placeholder="Acme Ltd." className="rounded-[var(--radius-sm)] focus-visible:ring-[hsl(221,83%,53%)] focus-visible:ring-[3px] focus-visible:border-[hsl(221,83%,53%)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="business_type" rules={{
        required: "Please select your business type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Business Type</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full min-w-0 rounded-[var(--radius-sm)] border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:opacity-50 md:text-sm focus-visible:border-[hsl(221,83%,53%)] focus-visible:ring-[hsl(221,83%,53%)] focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive">
                  <option value="">Select your business type</option>
                  <option value="Sole Trader">Sole Trader</option>
                  <option value="Limited Company">Limited Company</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Corporation">Corporation</option>
                  <option value="Other">Other</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel>How Can We Help?</FormLabel>
              <FormControl>
                <textarea placeholder="Briefly describe your accounting needs..." rows={4} className="flex w-full min-w-0 rounded-[var(--radius-sm)] border border-input bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:opacity-50 md:text-sm focus-visible:border-[hsl(221,83%,53%)] focus-visible:ring-[hsl(221,83%,53%)] focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-[hsl(0,84%,95%)] border border-[hsl(0,84%,70%)] rounded-[var(--radius-md)]">
            <p className="text-[hsl(0,84%,40%)] text-[14px]">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full rounded-[var(--radius-full)] h-10 bg-primary text-foreground-primary hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting...
            </> : 'Get Started'}
        </Button>
      </div>
    </Form>;
}